import { useTranslation } from 'react-i18next';
import { DownloadIcon, formModelGet } from '@portal/ui-library';
import { useState } from 'react';
import { cloneDeep } from 'lodash';

import classNames from 'classnames';
import { useHealthInsuranceContext } from 'contexts/HealthInsuranceContext';
import { downloadData, downloadDisclaimerDocument } from 'utils/fetchDocument';
import { WizardRoutesEnum } from 'utils/actions';
import axios from 'axios';
import {
  Application,
  ApplicationService,
  Partner,
  PersonRoleEnum
} from 'models/extension-generated';
import { DownloadOfferDialog } from './DownloadOfferDialog';
import { partnerField } from 'models';
import { Button } from '@mui/material';

type DownloadButtonProps = {
  currentPage: WizardRoutesEnum;
};

export const downloadOfferDocument = async (application: Application) => {
  const response = await axios.post(
    '/api/public/new/application/offer?downloadAccompanyDocs=false',
    { ...application, id: application.id },
    { responseType: 'blob' }
  );

  downloadData(response.data, `${application.id}-VS.pdf`);
};

export const DownloadButton = ({ currentPage }: DownloadButtonProps) => {
  const { t } = useTranslation('wizardPrivateHealthInsurance');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { state, reducer, partnerId } = useHealthInsuranceContext();

  const hasConsentPath = partnerField(partnerId, 'applicationInformation.acceptsDataProtection');
  const hasConsent = formModelGet(state.model, hasConsentPath);

  const handleConsentClick = (hasConsent: boolean) => {
    reducer.updateValue({
      key: hasConsentPath,
      value: hasConsent
    });
  };

  const downloadAccompanied = async () => {
    const cloned = { ...cloneDeep(state.model) };

    cloned.partners = cloned.partners.filter((p: Partner) => {
      if (p.roles.includes(PersonRoleEnum.INSURED_PERSON)) {
        // This is mandatory for insured partners to save in proposal
        return p.birthDate && p.gender;
      }
      return true;
    });
    const applicationId: string = await (async () => {
      if (cloned.id) {
        return cloned.id;
      }
      const { id } = await ApplicationService.createPublicApplication({ requestBody: cloned });
      reducer.updateValue({
        key: 'id',
        value: id
      });
      return id;
    })();

    await downloadOfferDocument({
      ...cloned,
      id: applicationId
    });
    setIsDialogOpen(false);
  };

  return (
    <>
      <DownloadOfferDialog
        hasConsent={hasConsent}
        onDownload={downloadAccompanied}
        onDownloadDisclaimerDocument={() =>
          downloadDisclaimerDocument(state.model.applicationStart)
        }
        onConsentClick={handleConsentClick}
        onClose={() => {
          setIsDialogOpen(false);
        }}
        isOpen={isDialogOpen}
      />

      <Button
        variant="contained"
        size="large"
        className={classNames('flex justify-center')}
        onClick={() => {
          setIsDialogOpen((prev) => !prev);
        }}
        startIcon={<DownloadIcon />}
      >
        {t('actions.downloadOffer.label')}
      </Button>
    </>
  );
};
